import CustomDialog from "../../common/CustomDialog";
import FormField from "../../common/FormField";
import CustomButton from "../../common/CustomButton";
import { useEffect, useState } from "react";
import useApiServices from "../../../utils/useApiServices";
import {
    CREATE_SURYODAY_SID_API,
    GET_ALL_ACTIVE_VEHICAL_MASTER_FOR_SID,
} from "../../../utils/constant";
import swal from "sweetalert";

export default function CreateSuryodaySIDForm({ open, onHandleClose, refetchData }) {
    const { apiCallFnforGet, apiCallFnforPost } = useApiServices();

    const [formData, setFormData] = useState({
        domainName: "",
        mid: "",
        username: "",
        password: "",
        vehicleName: "",
    });

    const [formErrors, setFormErrors] = useState({});
    const [vehicleOptions, setVehicleOptions] = useState([]);
    const [loadingVehicles, setLoadingVehicles] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const title = "Create SID";

    // Generate random Auth Token
    const generateAuthToken = (length = 24) => {
        const chars =
            "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        return Array.from({ length }, () =>
            chars.charAt(Math.floor(Math.random() * chars.length))
        ).join("");
    };

    // Handle field change
    const handleChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            [e?.target?.name]: e?.target?.value,
        }));
    };

    // Validate form before submission
    const validateForm = () => {
        let errors = {};
        if (!formData?.domainName?.trim())
            errors.domainName = "Domain name is required.";
        if (!formData?.mid?.trim()) errors.mid = "MID is required.";
        if (!formData?.username?.trim()) errors.username = "Username is required.";
        if (!formData?.password?.trim()) errors.password = "Password is required.";
        if (!formData?.vehicleName?.trim())
            errors.vehicleName = "Vehicle is required.";

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Submit handler
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            swal({
                title: "Error",
                text: "Please fill all required fields correctly!",
                icon: "error",
            });
            return;
        }

        setIsSubmitting(true);
        const payload = {
            ...formData,
            serviceProvider: "SURYODAY",
            authToken: generateAuthToken(),
        };

        try {
            const response = await apiCallFnforPost(CREATE_SURYODAY_SID_API, payload);
            if (response?.statusCode === 200) {
                swal({
                    title: "Success",
                    text: response?.message || "SID created successfully!",
                    icon: "success",
                });
                onHandleClose();
                refetchData();
            } else {
                swal({
                    title: "Error",
                    text: response?.message || "Failed to create SID",
                    icon: "error",
                });
            }
        } catch (error) {
            console.error("Error creating SID:", error);
            swal({
                title: "Error",
                text: "Something went wrong while creating SID!",
                icon: "error",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Fetch vehicle names
    useEffect(() => {
        const fetchVehicles = async () => {
            setLoadingVehicles(true);
            try {
                const response = await apiCallFnforGet(
                    GET_ALL_ACTIVE_VEHICAL_MASTER_FOR_SID
                );
                setVehicleOptions(response?.data || []);
            } catch (error) {
                console.error("Error fetching vehicles:", error);
            } finally {
                setLoadingVehicles(false);
            }
        };
        fetchVehicles();
    }, []);

    return (
        <CustomDialog
            open={open}
            onClose={onHandleClose}
            title={title}
            actions={
                <>
                    <CustomButton
                        variant="outlined"
                        label="Cancel"
                        onClick={onHandleClose}
                    />
                    <CustomButton
                        variant="contained"
                        label={isSubmitting ? "Saving..." : "Create SID"}
                        type="submit"
                        disabled={isSubmitting}
                        loading={isSubmitting}
                        onClick={handleFormSubmit}
                    />
                </>
            }
        >
            <div className="row">
                {/* Domain Name */}
                <div className="col-12 col-md-6">
                    <FormField
                        label="Domain Name"
                        name="domainName"
                        type="text"
                        value={formData?.domainName}
                        onChange={handleChange}
                        placeholder="Enter Domain Name"
                        required
                        error={formErrors?.domainName}
                    />
                </div>

                {/* MID */}
                <div className="col-12 col-md-6">
                    <FormField
                        label="MID"
                        name="mid"
                        type="text"
                        value={formData?.mid}
                        onChange={handleChange}
                        placeholder="Enter MID"
                        required
                        error={formErrors?.mid}
                    />
                </div>

                {/* Username */}
                <div className="col-12 col-md-6">
                    <FormField
                        label="Username"
                        name="username"
                        type="text"
                        value={formData?.username}
                        onChange={handleChange}
                        placeholder="Enter Username"
                        required
                        error={formErrors?.username}
                    />
                </div>

                {/* Password */}
                <div className="col-12 col-md-6">
                    <FormField
                        label="Password"
                        name="password"
                        type="password"
                        value={formData?.password}
                        onChange={handleChange}
                        placeholder="Enter Password"
                        required
                        error={formErrors?.password}
                    />
                </div>

                {/* Vehicle Dropdown */}
                <div className="col-12 col-md-6">
                    <label className="form-label mb-1">
                        Vehicle Name <span className="required">*</span>
                    </label>
                    <select
                        name="vehicleName"
                        className="form-control"
                        value={formData?.vehicleName}
                        onChange={handleChange}
                        disabled={loadingVehicles}
                        required
                    >
                        <option value="">Select Vehicle</option>
                        {vehicleOptions?.map((vehicle, index) => (
                            <option
                                key={index || vehicle}
                                value={vehicle}
                            >
                                {vehicle}
                            </option>
                        ))}
                    </select>
                    {formErrors?.vehicleName && (
                        <small className="text-danger">{formErrors?.vehicleName}</small>
                    )}
                </div>

                {/* Service Provider */}
                <div className="col-12 col-md-6">
                    <FormField
                        label="Service Provider"
                        name="serviceProvider"
                        type="text"
                        value="SURYODAY"
                        readOnly
                        required
                    />
                </div>
            </div>
        </CustomDialog>
    );
}